import { useState, useEffect } from "react";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../services/firebase";
import { StatsTimeSeries, TopicStats } from "../types";

/**
 * 期間内の年月リストを取得
 * @param from YYYY-MM-DD
 * @param to YYYY-MM-DD
 * @returns ["2025-10", "2025-11"]
 */
const getMonthsBetween = (from: string, to: string): string[] => {
  const months: string[] = [];
  const [startYear, startMonth] = from.split("-").map(Number);
  const [endYear, endMonth] = to.split("-").map(Number);

  let year = startYear;
  let month = startMonth;

  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push(`${year}-${String(month).padStart(2, "0")}`);

    month++;
    if (month > 12) {
      month = 1;
      year++;
    }
  }

  return months;
};

/**
 * Firestore から時系列統計を直接取得
 */
const fetchStatsTimeSeriesFromFirestore = async (
  from: string,
  to: string
): Promise<StatsTimeSeries> => {
  const months = getMonthsBetween(from, to);

  const dailyData: Array<{
    date: string;
    totalResponses: number;
    byGenre: { [genre: string]: number };
    byDifficulty: { [difficulty: number]: number };
    byTopic: { [topicId: string]: TopicStats };
  }> = [];

  // 各月のデータを取得
  for (const month of months) {
    try {
      const monthCollectionRef = collection(db, "stats", "daily", month);
      const monthQuery = query(
        monthCollectionRef,
        where("date", ">=", from),
        where("date", "<=", to),
        orderBy("date", "asc")
      );

      const snapshot = await getDocs(monthQuery);

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        dailyData.push({
          date: data.date,
          totalResponses: data.totalResponses || 0,
          byGenre: data.byGenre || {},
          byDifficulty: data.byDifficulty || {},
          byTopic: data.byTopic || {},
        });
      });
    } catch (error) {
      console.error(`Error fetching time series for month ${month}:`, error);
    }
  }

  // 日付でソート
  dailyData.sort((a, b) => a.date.localeCompare(b.date));

  return {
    dateRange: {
      from,
      to,
    },
    dailyData,
  };
};

export const useStatsTimeSeries = (from: string, to: string) => {
  const [data, setData] = useState<StatsTimeSeries | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const result = await fetchStatsTimeSeriesFromFirestore(from, to);
        setData(result);
        setError(null);
      } catch (err) {
        setError(err as Error);
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (from && to) {
      fetchData();
    }
  }, [from, to]);

  return { data, isLoading, error };
};
